import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import isNumber from 'lodash.isnumber';
import isString from 'lodash.isstring';
import {BasicPage} from 'src/components/basic-page';
import {RequiredMark} from 'src/components/required-mark';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {WidthItem} from 'src/components/width-item';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {ValidationContextProvider} from 'src/contexts/validation-context';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {
    EntitlementPresetsCombobox,
} from 'src/modules/profile/account/tabs/settings/sections/entitlement-settings/entitlement-presets-combobox';
import {AccountPreset} from 'src/modules/profile/account/tabs/settings/sections/entitlement-settings/useAccountPreset';
import {LicenseType} from 'src/openapi/admin/models/LicenseType';

import Button from '@pb/kit-component-button';
import FormSection from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';

import {AccountInfo} from './components/account-info';
import {AccountTeam} from './components/account-team';
import {ConferenceInformation} from './components/conference-information';
import {LicenseDuration} from './components/license-duration';
import {useCreateConference} from './useCreateConference';


import type {AccessRight} from 'src/hooks/access-rights/constants';

const SECTION_NAME = 'Create New Conference';

const CreatePreparedConference = () => {
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const {setErrorMessage} = useContext(MessageContext);
    const {createConferenceOpen, editConferenceSubmit} = useEventTracking();


    const {
        setIsChunkChanged,
        proceed,
        isCurrentChunkChanged,
    } = useConfirmationChunk<number>({
        chunkName: 'create-new-conference',
        onProceed: conferenceAccountId => {
            if (conferenceAccountId) {
                navigate(`/profile/account/${conferenceAccountId}/conference`);
            }
        },
    });

    const {
        data: conference,
        setData: setConference,
        isChanged: isConferenceChanged,
        saveChanges: saveConference,
        validateChanges: validateConference,
    } = useCreateConference();

    useEffect(() => {
        setIsChunkChanged(isConferenceChanged);
    }, [isConferenceChanged, setIsChunkChanged]);

    useEffect(() => {
        createConferenceOpen();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const goToPreviousPage = useCallback(() => {
        editConferenceSubmit({submit: false, success: true});
        navigate(-1);
    }, [editConferenceSubmit, navigate]);

    const createConference = useCallback(async () => {
        const preSaveValidationError = validateConference();
        if (preSaveValidationError) {
            setErrorMessage(preSaveValidationError);
            return;
        }

        setBusy(true);
        const accountCreationResult = await saveConference();
        setBusy(false);

        if (accountCreationResult && isString(accountCreationResult)) {
            setErrorMessage(accountCreationResult);
            editConferenceSubmit({submit: true, success: false});
        } else if (accountCreationResult && isNumber(accountCreationResult)) {
            proceed(accountCreationResult);
            editConferenceSubmit({submit: true, success: true});
        }

    }, [saveConference, validateConference, editConferenceSubmit, proceed, setErrorMessage]);

    const handlePresetChange = useCallback((selectedPreset: AccountPreset) => {
        setConference(prevState => ({...prevState, preset: selectedPreset}));
    }, [setConference]);

    return (
        <>
            <ServerResponseHandler
                sectionName={SECTION_NAME}
                serverError={null}
                accessRight={ACCESS_RIGHTS.CONFERENCE_SETUP as AccessRight}>
                <BasicPage
                    title={SECTION_NAME}
                    buttonsBlock={(
                        <Group>
                            <Button
                                variant="Type.TERTIARY"
                                onClick={goToPreviousPage}>
                                Cancel
                            </Button>
                            <Button
                                variant="Type.PRIMARY"
                                disabled={!isCurrentChunkChanged}
                                busy={busy}
                                onClick={createConference}>
                                Create
                            </Button>
                        </Group>
                    )}>
                    <Item className="group-item pd-l">
                        <Group direction="v" gap="xl">
                            <AccountInfo
                                conferenceInfo={conference}
                                setConferenceInfo={setConference}
                            />
                            <AccountTeam {...{
                                accountTeamMembers: conference.managerRelations,
                                setAccountTeamMembers: setConference,
                                onFieldChange: () => undefined,
                            }}
                            />
                            <FormSection headline="Entitlements settings">
                                <WidthItem width={300}>
                                    <Label htmlFor="preset-suggest">
                                        <RequiredMark label="Entitlement preset"/>
                                    </Label>
                                    <EntitlementPresetsCombobox
                                        preset={conference.preset}
                                        setPreset={handlePresetChange}
                                        licenseType={LicenseType.CN}
                                        disabled={true}
                                    />
                                </WidthItem>
                            </FormSection>

                            <LicenseDuration
                                data={conference}
                                setData={setConference}
                                trackParameterChange={() => undefined}
                            />
                            <ConferenceInformation
                                data={conference}
                                setData={setConference}
                                trackParameterChange={() => undefined}
                            />
                        </Group>
                    </Item>
                </BasicPage>
            </ServerResponseHandler>
        </>
    );
};

export const ValidatedCreatePreparedConference = () => (
    <ValidationContextProvider>
        <CreatePreparedConference/>
    </ValidationContextProvider>
);
